import React, { useCallback, useEffect } from 'react';
import { Upload, Table, Row, Col, Button } from 'antd';
import { UploadOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import { UploadedFile } from '../../utils';
import { UploadChangeParam } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';

interface UploadWithTableProps {
  onFilesChange: React.Dispatch<any>;
  files?: UploadedFile[];
  disabled?: boolean;
  downloadFiles?: boolean;
}
const UploadWithTable: React.FC<UploadWithTableProps> = ({
  onFilesChange,
  files,
  disabled = false,
  downloadFiles = false,
}) => {

  const columns = [
    {
      title: 'Item',
      dataIndex: 'index',
      key: 'index',
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <div
          style={{
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            maxWidth: '300px',
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: 'Acciones',
      key: 'delete',
      render: (_: any, record: UploadedFile) =>
        !disabled ? (
          <Button
            type="link"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(String(record.key || record.id))}
          />
        ) :
          downloadFiles ? (
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
            />
          ) : null
    },
  ];

  const toUploadFile = (file: UploadedFile, idx: number): UploadFile => ({
    uid: String(file.key ?? file.id ?? file.name ?? idx),
    name: file.name,
    status: 'done',
    url: file.url,
    ...(file.file &&
      typeof file.file === 'object' &&
      'uid' in file.file &&
      'lastModifiedDate' in file.file &&
      file.file.lastModifiedDate instanceof Date && {
      originFileObj: file.file as import('antd/es/upload/interface').RcFile,
    }),
  });

  const handleDownload = (file: any) => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = (keyOrId: string) => {
    const updatedFiles = files?.filter((file) => {
      if (file.key) return String(file.key) !== keyOrId;
      if (file.id) return String(file.id) !== keyOrId;
      return true;
    });

    onFilesChange(updatedFiles || []);
  };

  const handleUploadChange = useCallback(({ fileList }: UploadChangeParam) => {
    const newFiles = fileList.map((fileMap) => {
      const fileExtension = fileMap.name.split('.').pop() || 'Desconocido';
      return {
        key: fileMap.uid,
        name: fileMap.name,
        type: fileExtension.toUpperCase(),
        file: fileMap.originFileObj,
      };
    });
    onFilesChange(newFiles);
  }, [onFilesChange]);
  return (
    <>
      <Row>
        <Col span={24}>
          <Upload.Dragger
            name="files"
            multiple
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleUploadChange}
            disabled={disabled}
            fileList={(files || []).map(toUploadFile)}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              style={{ marginBottom: '10px' }}
              disabled={disabled}
            >
              Subir archivos
            </Button>
            <p className="ant-upload-hint">
              Arrastre los archivos y suéltelos aquí
            </p>
          </Upload.Dragger>
        </Col>
      </Row>
      <Table
        dataSource={files}
        columns={columns}
        pagination={false}
        className="mt-16"
      />
    </>
  );
};

export default UploadWithTable;
